import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { useWizard, Wizard, WizardProps } from 'react-use-wizard';

interface WizardDataContextType<T> {
  data: T;
  setData: (partialData: Partial<T>) => void;
  setDataOnly: (partialData: Partial<T>) => void;
  clearData: () => void;
}

const WizardDataContext = createContext<WizardDataContextType<any> | null>(null);

export interface WizardWithDataProps<T extends Record<string, any>> extends Omit<
  WizardProps,
  'children'
> {
  initialData?: T;
  children: ReactNode;
}

/**
 * Component that wraps the react-use-wizard Wizard with data persistence capabilities.
 * Provides a context for child components to access and modify wizard data.
 */
export function WizardWithData<T extends Record<string, any>>({
  initialData = {} as T,
  children,
  ...wizardProps
}: WizardWithDataProps<T>) {
  const [data, setDataState] = useState<T>(initialData);

  const setData = useCallback((partialData: Partial<T>) => {
    setDataState((prevData) => ({
      ...prevData,
      ...partialData,
    }));
  }, []);

  const clearData = useCallback(() => {
    setDataState(initialData);
  }, [initialData]);

  const contextValue: WizardDataContextType<T> = {
    data,
    setData,
    setDataOnly: setData, // Same function, just different name for clarity
    clearData,
  };

  return (
    <WizardDataContext.Provider value={contextValue}>
      <Wizard {...wizardProps}>{children}</Wizard>
    </WizardDataContext.Provider>
  );
}

/**
 * Hook to access wizard data from any component within a WizardWithData context.
 * Combines useWizard functionality with data management.
 */
export function useWizardWithData<T extends Record<string, any>>(): ReturnType<typeof useWizard> &
  WizardDataContextType<T> {
  const wizard = useWizard();
  const dataContext = useContext(WizardDataContext);

  if (!dataContext) {
    throw new Error('useWizardWithData must be used within a WizardWithData component');
  }

  // Enhanced setData that automatically moves to next step
  const setDataAndNext = useCallback(
    (partialData: Partial<T>) => {
      dataContext.setData(partialData);
      wizard.nextStep();
    },
    [dataContext, wizard],
  );

  return {
    ...wizard,
    data: dataContext.data,
    setData: setDataAndNext, // Auto-advances to next step
    setDataOnly: dataContext.setDataOnly, // Just sets data without advancing
    clearData: dataContext.clearData,
  };
}
